// LDT-711 (épica LDT-698) · El dato de salud de una reserva o inscripción
// (`health_notes`: alergias y necesidades especiales, art. 9 RGPD) no puede
// llegar a Sentry desde el navegador. El SDK no manda el cuerpo de las
// peticiones por defecto, pero un `extra`, un `context`, los datos de una
// breadcrumb o un `request.data` los pone quien llama, y un descuido basta.
// Defensa de clase, como `_shared/sentry.ts` en el servidor: toda clave cuyo
// nombre sugiera salud se tapa entera, a cualquier profundidad.
//
// Lo usan `beforeSend` y `beforeBreadcrumb` en src/main.tsx; lo prueba
// src/test/sentry-sin-dato-de-salud.test.ts.

/** Claves que se tapan: health*, salud, alerg* / allerg* (es/ca/en). */
export const HEALTH_KEY_RE = /health|salud|salut|alerg|al·lerg|allerg/i;

export const REDACTED = "<redactado>";

const MAX_DEPTH = 8;

/** Copia `value` tapando toda clave de salud. No muta el original. */
export function scrubHealthKeys<T>(value: T, depth = 0): T {
  if (depth > MAX_DEPTH || value === null || typeof value !== "object") return value;
  if (Array.isArray(value)) return value.map((v) => scrubHealthKeys(v, depth + 1)) as unknown as T;
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
    out[k] = HEALTH_KEY_RE.test(k) ? REDACTED : scrubHealthKeys(v, depth + 1);
  }
  return out as T;
}

/** Un cuerpo de petición en texto (JSON) con claves de salud: se tapa. */
function scrubBody(data: unknown): unknown {
  if (typeof data === "string") {
    try {
      return JSON.stringify(scrubHealthKeys(JSON.parse(data)));
    } catch {
      return HEALTH_KEY_RE.test(data) ? REDACTED : data;
    }
  }
  return scrubHealthKeys(data);
}

interface EventLike {
  extra?: unknown;
  contexts?: unknown;
  tags?: unknown;
  request?: { data?: unknown };
  breadcrumbs?: Array<{ data?: unknown }>;
}

/** `beforeSend`: el evento sin datos de salud en extra, contexts, tags, request.data ni breadcrumbs. */
export function scrubHealthEvent<E extends object>(event: E): E {
  const out = { ...event } as EventLike;
  if (out.extra) out.extra = scrubHealthKeys(out.extra);
  if (out.contexts) out.contexts = scrubHealthKeys(out.contexts);
  if (out.tags) out.tags = scrubHealthKeys(out.tags);
  if (out.request && "data" in out.request) out.request = { ...out.request, data: scrubBody(out.request.data) };
  if (Array.isArray(out.breadcrumbs)) out.breadcrumbs = out.breadcrumbs.map(scrubHealthBreadcrumb);
  return out as unknown as E;
}

/** `beforeBreadcrumb`: los datos de la miga sin claves de salud. */
export function scrubHealthBreadcrumb<B extends { data?: unknown }>(crumb: B): B {
  return crumb && crumb.data ? { ...crumb, data: scrubBody(crumb.data) } : crumb;
}
